import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import adminSchemaApi from '../api/adminSchemaApi';

/**
 * Sandbox → Production migration modal.
 * Modes: schema | full | metadata
 */
const AdminProdMigrationModal = ({ show, onHide, companyId, schemaName }) => {
  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [mode, setMode] = useState('schema');
  const [copyData, setCopyData] = useState(false);
  const [includeReports, setIncludeReports] = useState(true);
  const [includeDashboards, setIncludeDashboards] = useState(true);
  const [includeHomeLayouts, setIncludeHomeLayouts] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const loadStatus = useCallback(async () => {
    if (!companyId && !schemaName) return;
    setStatusLoading(true);
    setError('');
    try {
      const data = await adminSchemaApi.fetchProdMigrationStatus({
        companyId: companyId || undefined,
        schema: schemaName || undefined,
      });
      setStatus(data);
    } catch (e) {
      setStatus(null);
      setError(e?.message || 'Failed to load production status');
    } finally {
      setStatusLoading(false);
    }
  }, [companyId, schemaName]);

  useEffect(() => {
    if (!show) return;
    setResult(null);
    setError('');
    setMode('schema');
    setCopyData(false);
    loadStatus();
  }, [show, loadStatus]);

  const handleRun = async () => {
    setRunning(true);
    setError('');
    setResult(null);
    try {
      const data = await adminSchemaApi.runProdMigration({
        companyId,
        schema: schemaName,
        mode,
        copyData: mode === 'full' ? copyData : false,
        includeReports: mode === 'metadata' ? includeReports : true,
        includeDashboards: mode === 'metadata' ? includeDashboards : true,
        includeHomeLayouts: mode === 'metadata' ? includeHomeLayouts : true,
      });
      setResult(data);
      await loadStatus();
    } catch (e) {
      setError(e?.message || 'Production migration failed');
    } finally {
      setRunning(false);
    }
  };

  const schema = status?.schema || schemaName || '';
  const configured = status?.configured !== false;
  const canRun = configured && status?.sourceExists !== false && !running && !statusLoading;

  return (
    <Modal show={show} onHide={onHide} centered size="lg" backdrop={running ? 'static' : true}>
      <Modal.Header closeButton={!running}>
        <Modal.Title className="admin-title">Migrate to Production</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="admin-subtitle small mb-3">
          Copy from the current (sandbox) database to production. Schema name must match on both sides:
          {' '}
          <strong>{schema || '—'}</strong>
        </p>

        {statusLoading && (
          <div className="text-muted small mb-3">
            <Spinner animation="border" size="sm" className="me-2" />
            Checking production…
          </div>
        )}

        {error && (
          <Alert variant="danger" dismissible onClose={() => setError('')} className="mb-3">
            {error}
          </Alert>
        )}

        {status && !statusLoading && (
          <Alert variant={status.prodSchemaExists ? 'warning' : 'info'} className="mb-3 small">
            {!configured && <div>{(status.warnings || [])[0] || 'Production DB is not configured.'}</div>}
            {configured && (
              <>
                <div>
                  Prod DB: {status.prod?.host || '—'} / {status.prod?.database || '—'}
                </div>
                <div className="mt-1">
                  {status.prodSchemaExists
                    ? `Schema "${schema}" already exists on production — migration will merge (existing rows skipped).`
                    : `Schema "${schema}" will be created on production with the same name.`}
                </div>
                {(status.missingTables || []).length > 0 && (
                  <div className="mt-1">
                    {status.missingTables.length} missing table(s) on prod will be created during merge.
                  </div>
                )}
              </>
            )}
          </Alert>
        )}

        <Form.Group className="mb-3">
          <Form.Label className="fw-semibold small">Migration mode</Form.Label>
          <div>
            <Form.Check
              type="radio"
              id="prod-mode-schema"
              name="prodMode"
              label="Schema only — structure (tables/columns) on production"
              checked={mode === 'schema'}
              onChange={() => setMode('schema')}
              disabled={running}
            />
            <Form.Check
              type="radio"
              id="prod-mode-full"
              name="prodMode"
              label="Full migration — structure + config + reports/dashboards/home layouts"
              checked={mode === 'full'}
              onChange={() => setMode('full')}
              disabled={running}
            />
            <Form.Check
              type="radio"
              id="prod-mode-metadata"
              name="prodMode"
              label="Reports / Dashboards / Home Layouts only"
              checked={mode === 'metadata'}
              onChange={() => setMode('metadata')}
              disabled={running}
            />
          </div>
        </Form.Group>

        {mode === 'full' && (
          <Form.Check
            type="checkbox"
            id="prod-copy-data"
            className="mb-3"
            label="Copy CRM record data (leads, accounts, opportunities, etc.)"
            checked={copyData}
            onChange={(e) => setCopyData(e.target.checked)}
            disabled={running}
          />
        )}

        {mode === 'metadata' && (
          <div className="mb-3 ps-1">
            <Form.Check
              type="checkbox"
              id="prod-inc-reports"
              label="Reports"
              checked={includeReports}
              onChange={(e) => setIncludeReports(e.target.checked)}
              disabled={running}
            />
            <Form.Check
              type="checkbox"
              id="prod-inc-dashboards"
              label="Dashboards"
              checked={includeDashboards}
              onChange={(e) => setIncludeDashboards(e.target.checked)}
              disabled={running}
            />
            <Form.Check
              type="checkbox"
              id="prod-inc-home"
              label="Home Layouts"
              checked={includeHomeLayouts}
              onChange={(e) => setIncludeHomeLayouts(e.target.checked)}
              disabled={running}
            />
          </div>
        )}

        {result && (
          <Alert variant={result.failed > 0 || (result.crmData?.failed || 0) > 0 ? 'warning' : 'success'} className="small">
            <div style={{ whiteSpace: 'pre-wrap' }}>{result.message}</div>
            {(result.warnings || []).length > 0 && (
              <ul className="mb-0 mt-2 ps-3">
                {result.warnings.slice(0, 15).map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            )}
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide} disabled={running}>
          Close
        </Button>
        <Button className="admin-primary-btn" onClick={handleRun} disabled={!canRun}>
          {running ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Migrating…
            </>
          ) : (
            'Run production migration'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AdminProdMigrationModal;
